import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Mic, MicOff, Square, Copy, Check, Upload, Loader2, FileAudio, Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const SpeechToText = () => {
  const { token, API } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimText, setInterimText] = useState('');
  const [copied, setCopied] = useState(false);
  const [supported, setSupported] = useState(false);
  const [lang, setLang] = useState('en-US');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const recognitionRef = useRef(null);
  const fileRef = useRef(null);

  const LANGUAGES = [
    { code: 'en-US', label: 'English (US)' },
    { code: 'en-GB', label: 'English (UK)' },
    { code: 'zh-CN', label: 'Chinese (Simplified)' },
    { code: 'zh-TW', label: 'Chinese (Traditional)' },
    { code: 'es-ES', label: 'Spanish' },
    { code: 'fr-FR', label: 'French' },
    { code: 'de-DE', label: 'German' },
    { code: 'it-IT', label: 'Italian' },
    { code: 'pt-BR', label: 'Portuguese (Brazil)' },
    { code: 'ja-JP', label: 'Japanese' },
    { code: 'ko-KR', label: 'Korean' },
    { code: 'ar-SA', label: 'Arabic' },
    { code: 'hi-IN', label: 'Hindi' },
    { code: 'ru-RU', label: 'Russian' },
    { code: 'bn-BD', label: 'Bengali' },
  ];

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SpeechRecognition);
    return () => { recognitionRef.current?.stop(); };
  }, []);

  const startRecording = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Speech recognition not supported. Use Chrome or Edge.'); return; }
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;
    recognition.onresult = (e) => {
      let final = transcript;
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) { final += e.results[i][0].transcript + ' '; }
        else { interim += e.results[i][0].transcript; }
      }
      setTranscript(final);
      setInterimText(interim);
    };
    recognition.onerror = (e) => {
      if (e.error !== 'no-speech') toast.error(`Error: ${e.error}`);
      setIsRecording(false);
    };
    recognition.onend = () => { setIsRecording(false); setInterimText(''); };
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
    toast.success('Recording started');
  };

  const stopRecording = () => {
    recognitionRef.current?.stop();
    setIsRecording(false);
    setInterimText('');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(transcript);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied!');
  };

  const handleClear = () => { setTranscript(''); setInterimText(''); setUploadedFile(null); };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Max 50MB.');
      return;
    }
    
    setUploadedFile(file);
    setUploadLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', file);
      formData.append('language', lang.split('-')[0]);
      
      const res = await axios.post(`${API}/transcribe`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      });
      
      if (res.data.transcript) {
        setTranscript(prev => prev + res.data.transcript);
        toast.success('Transcription complete!');
      } else if (res.data.status === 'browser_required') {
        toast.info('File uploaded. Use microphone for real-time transcription for best results.');
      } else if (res.data.error) {
        toast.error(res.data.error);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transcription failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const fullText = transcript + interimText;
  const wordCount = transcript.split(/\s+/).filter(w => w).length;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="glass-card rounded-2xl p-6 border border-border">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <FileAudio className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-bold font-outfit text-foreground text-lg">Speech to Text</h2>
            <p className="text-xs text-muted-foreground">Real-time AI transcription from microphone or file upload</p>
          </div>
        </div>

        {!supported && (
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-4">
            <p className="text-sm text-amber-500">
              Live speech recognition requires Chrome or Edge browser. File upload transcription still works.
            </p>
          </div>
        )}

        {/* Language select */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-foreground mb-1.5">Language</label>
          <select
            data-testid="stt-lang-select"
            value={lang}
            onChange={e => setLang(e.target.value)}
            disabled={isRecording}
            className="w-full px-4 py-2.5 rounded-xl bg-background border border-border text-foreground text-sm focus:border-primary"
          >
            {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>

        {/* Controls */}
        <div className="flex gap-3">
          {!isRecording ? (
            <button
              data-testid="start-recording-btn"
              onClick={startRecording}
              disabled={!supported}
              className="flex-1 gradient-btn py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Mic className="w-5 h-5" /> Start Recording
            </button>
          ) : (
            <button
              data-testid="stop-recording-btn"
              onClick={stopRecording}
              className="flex-1 py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white transition-colors"
            >
              <Square className="w-5 h-5" fill="white" /> Stop Recording
            </button>
          )}
          <button
            data-testid="upload-file-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploadLoading}
            className="px-4 py-4 rounded-xl bg-secondary flex items-center gap-2 text-sm text-foreground hover:bg-secondary/70 transition-colors disabled:opacity-50"
            title="Upload audio file"
          >
            {uploadLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          </button>
          <input ref={fileRef} type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac" className="hidden" onChange={handleFileUpload} />
        </div>

        {/* Recording indicator */}
        {isRecording && (
          <div className="mt-4 flex items-center gap-2 text-sm text-red-500">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            Listening... Speak clearly into your microphone
          </div>
        )}

        {/* Upload progress */}
        {uploadLoading && (
          <div className="mt-4 flex items-center gap-2 text-sm text-primary">
            <Loader2 className="w-4 h-4 animate-spin" />
            Transcribing {uploadedFile?.name}...
          </div>
        )}

        {/* Uploaded file info */}
        {uploadedFile && !uploadLoading && (
          <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
            <FileAudio className="w-3.5 h-3.5" />
            {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(0)} KB)
          </div>
        )}
      </div>

      {/* Transcript output */}
      <div className="glass-card rounded-2xl p-6 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold font-outfit text-foreground">Transcript</h3>
            <p className="text-xs text-muted-foreground">{wordCount} words · {transcript.length} characters</p>
          </div>
          <div className="flex gap-2">
            {transcript && (
              <>
                <button onClick={handleCopy} data-testid="copy-transcript-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-secondary text-xs text-foreground hover:bg-secondary/70 transition-colors">
                  {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <button onClick={handleClear} data-testid="clear-transcript-btn"
                  className="px-3 py-1.5 rounded-xl bg-secondary text-xs text-muted-foreground hover:bg-secondary/70 transition-colors">
                  Clear
                </button>
              </>
            )}
          </div>
        </div>

        <div
          data-testid="transcript-output"
          className="min-h-40 p-4 rounded-xl bg-background border border-border text-sm text-foreground leading-relaxed"
        >
          {!fullText && !isRecording && (
            <p className="text-muted-foreground text-center mt-8">
              Click "Start Recording" or upload an audio file to see your transcription here
            </p>
          )}
          <span className="text-foreground">{transcript}</span>
          {interimText && <span className="text-muted-foreground italic">{interimText}</span>}
          {isRecording && !fullText && (
            <span className="text-muted-foreground animate-pulse">Waiting for speech...</span>
          )}
        </div>

        {transcript && (
          <div className="mt-3 flex gap-3">
            <button
              data-testid="download-txt-btn"
              onClick={() => {
                const blob = new Blob([transcript], { type: 'text/plain' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `transcript-${Date.now()}.txt`;
                a.click();
              }}
              className="flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <Download className="w-3 h-3" /> Download .txt
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeechToText;
